import type { OverlayItem, TimelineProject } from "@scroll/shared-types";

export const REPLY_PERMISSION_VALUES = [
  "everyone",
  "following",
  "mentioned",
  "none",
] as const;

export type ReplyPermission = (typeof REPLY_PERMISSION_VALUES)[number];

export type DraftKind = "update" | "episode" | "video-project";

export interface EpisodeDraftData {
  /** "none" = closed from pristine Step 1 (nothing selected yet). */
  sourceType: "none" | "audio-record" | "audio-file" | "video";
  audioUri?: string;
  /** Original picker name keeps the RSS-ready extension across draft copies. */
  audioFileName?: string;
  audioDurationSeconds?: number;
  videoUri?: string;
  /** Durable LOCAL uri — upload happens only at publish. */
  artworkLocalUri?: string;
  generateCaptions: boolean;
  selectedCreatorPodcastId: number | null;
  details: {
    title: string;
    feedPostText: string;
    description: string;
    chapters: { timestamp: string; label: string }[];
  };
}

export interface PostDraft {
  id: string;
  content: string;
  imageDataUrl: string | null;
  replyPermission: ReplyPermission;
  createdAt: string;
  updatedAt: string;
  imageUris?: string[];
  imageDimensions?: Array<{ width: number; height: number }>;
  altTexts?: Record<string, string>;
  videoUri?: string;
  videoDimensions?: { width: number; height: number };
  videoDuration?: number;
  videoTimelineProject?: TimelineProject;
  videoLinkOverlays?: OverlayItem[];
  videoVoiceover?: {
    url: string;
    volume: number;
    originalAudioVolume: number;
  } | null;
  videoCaption?: string;
  showPoll?: boolean;
  pollOptions?: string[];
  pollDuration?: number;
  /** Versioned discriminator — written by every save path going forward.
   *  Absent on legacy drafts, which classify via heuristics (classifyDraft). */
  draftKind?: DraftKind;
  episode?: EpisodeDraftData;
  /** Show scope for update drafts. Absent = All shows (never persisted as 0/null). */
  targetCreatorPodcastId?: number;
}

export interface ComposePoll {
  options: string[];
  duration: number;
}

export type ComposeImageFieldMode = "single-or-multi" | "multi-only";

interface HasDraftContentInput {
  content: string;
  imageCount?: number;
  hasVideo?: boolean;
  showPoll?: boolean;
}

interface BuildComposePollInput {
  enabled: boolean;
  options: string[];
  duration: number;
}

interface BuildPostDraftInput {
  content: string;
  imageDataUrl: string | null;
  replyPermission?: string | null;
  imageUris?: string[];
  imageDimensions?: Array<{ width: number; height: number }>;
  altTexts?: Record<string, string>;
  videoUri?: string;
  videoDimensions?: { width: number; height: number };
  videoDuration?: number;
  videoTimelineProject?: TimelineProject;
  videoLinkOverlays?: OverlayItem[];
  videoVoiceover?: PostDraft["videoVoiceover"];
  videoCaption?: string;
  showPoll?: boolean;
  pollOptions?: string[];
  pollDuration?: number;
  targetCreatorPodcastId?: number | null;
}

interface GetDraftPollStateInput {
  draft: PostDraft;
  defaultPollDuration: number;
}

interface OptimisticPostUser {
  id?: string | null;
  username?: string | null;
  displayName?: string | null;
  profileImageUrl?: string | null;
  isVerified?: boolean | null;
  subscriptionTier?: string | null;
}

interface BuildOptimisticPostItemInput {
  content: string;
  imageUrls?: string[];
  user?: OptimisticPostUser | null;
}

interface BuildDraftRestoreStateInput {
  draft: PostDraft;
  defaultPollDuration: number;
}

interface VideoUploadFilterState {
  filterId: string | null;
}

interface VideoUploadOverlayState {
  textOverlays: unknown[];
}

interface BuildVideoUploadMetadataInput {
  caption: string;
  filterState: VideoUploadFilterState;
  overlayState: VideoUploadOverlayState;
}

interface BuildComposePostBodyInput<
  Extra extends Record<string, unknown> = Record<string, never>,
> {
  content: string;
  replyPermission?: string | null;
  timezoneOffset?: number;
  imageUrls?: string[];
  imageFieldMode?: ComposeImageFieldMode;
  altTexts?: string[];
  poll?: ComposePoll;
  /**
   * Explicit link to bind a preview/embed even when the URL was removed from the
   * caption text (X/Twitter-style "delete the URL, keep the card").
   */
  embedUrl?: string | null;
  extra?: Extra;
}

export const VIDEO_UPLOAD_UPSELL_DISMISSED_KEY = "scroll_upload_upsell_dismissed";

export function normalizeReplyPermission(
  value: string | null | undefined,
): ReplyPermission {
  if (value === "followers") return "following";
  if (
    value === "everyone" ||
    value === "following" ||
    value === "mentioned" ||
    value === "none"
  ) {
    return value;
  }
  return "everyone";
}

export function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function buildComposePoll({
  enabled,
  options,
  duration,
}: BuildComposePollInput): ComposePoll | undefined {
  if (!enabled) return undefined;
  const trimmedOptions = options.map((option) => option.trim()).filter(Boolean);
  if (trimmedOptions.length < 2) return undefined;
  return {
    options: trimmedOptions,
    duration,
  };
}

export function hasDraftContent({
  content,
  imageCount = 0,
  hasVideo = false,
  showPoll = false,
}: HasDraftContentInput): boolean {
  return Boolean(content.trim() || imageCount > 0 || hasVideo || showPoll);
}

/** Valid show scope = a positive integer. Guards BOTH persistence (never write
 *  0/NaN/Infinity noise) and restore (storage-backed drafts may carry junk). */
function sanitizeDraftShowTarget(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : null;
}

export function buildPostDraft({
  content,
  imageDataUrl,
  replyPermission,
  imageUris,
  imageDimensions,
  altTexts,
  videoUri,
  videoDimensions,
  videoDuration,
  videoTimelineProject,
  videoLinkOverlays,
  videoVoiceover,
  videoCaption,
  showPoll = false,
  pollOptions,
  pollDuration,
  targetCreatorPodcastId,
}: BuildPostDraftInput): Omit<PostDraft, "id" | "createdAt" | "updatedAt"> {
  const poll = buildComposePoll({
    enabled: showPoll,
    options: pollOptions ?? [],
    duration: pollDuration ?? 0,
  });

  // Video-bearing payloads are video-projects (the dormant editor's domain) —
  // stamping them "update" would misroute them to the video-less update sheet.
  const draftKind: DraftKind =
    videoTimelineProject ||
    videoUri ||
    videoDimensions ||
    typeof videoDuration === "number" ||
    videoVoiceover ||
    videoCaption?.trim() ||
    videoLinkOverlays?.length
      ? "video-project"
      : "update";

  return {
    content: content.trim(),
    imageDataUrl,
    replyPermission: normalizeReplyPermission(replyPermission),
    draftKind,
    ...(imageUris?.length ? { imageUris } : {}),
    ...(imageDimensions?.length ? { imageDimensions } : {}),
    ...(altTexts && Object.keys(altTexts).length ? { altTexts } : {}),
    ...(videoUri ? { videoUri } : {}),
    ...(videoDimensions ? { videoDimensions } : {}),
    ...(typeof videoDuration === "number" ? { videoDuration } : {}),
    ...(videoTimelineProject ? { videoTimelineProject } : {}),
    ...(videoLinkOverlays?.length ? { videoLinkOverlays } : {}),
    ...(videoVoiceover ? { videoVoiceover } : {}),
    ...(videoCaption?.trim() ? { videoCaption: videoCaption.trim() } : {}),
    ...(sanitizeDraftShowTarget(targetCreatorPodcastId) !== null
      ? { targetCreatorPodcastId: sanitizeDraftShowTarget(targetCreatorPodcastId)! }
      : {}),
    ...(poll
      ? {
          showPoll: true,
          pollOptions: poll.options,
          pollDuration: poll.duration,
        }
      : {}),
  };
}

export function getDraftPollState({
  draft,
  defaultPollDuration,
}: GetDraftPollStateInput): {
  showPoll: boolean;
  pollOptions: string[];
  pollDuration: number;
} {
  const poll = buildComposePoll({
    enabled: Boolean(draft.showPoll),
    options: draft.pollOptions ?? [],
    duration: draft.pollDuration ?? defaultPollDuration,
  });

  if (!poll) {
    return {
      showPoll: false,
      pollOptions: ["", ""],
      pollDuration: defaultPollDuration,
    };
  }

  return {
    showPoll: true,
    pollOptions: poll.options,
    pollDuration: poll.duration,
  };
}

export function buildOptimisticPostItem({
  content,
  imageUrls = [],
  user,
}: BuildOptimisticPostItemInput) {
  const now = Date.now();

  return {
    type: "post" as const,
    data: {
      id: -now,
      postId: `optimistic-${now}`,
      userId: user?.id || "",
      content: content.trim(),
      imageUrl: imageUrls.length === 1 ? imageUrls[0] : null,
      imageUrls: imageUrls.length > 1 ? imageUrls : null,
      likes: 0,
      repostsCount: 0,
      liked: false,
      reposted: false,
      createdAt: new Date(now).toISOString(),
      user: {
        id: user?.id || "",
        username: user?.username || null,
        displayName: user?.displayName || null,
        profileImageUrl: user?.profileImageUrl || null,
        isVerified: user?.isVerified ?? false,
        subscriptionTier: user?.subscriptionTier ?? null,
      },
      linkPreviews: [],
      poll: null,
    },
  };
}

export function getMaxUploadVideoDurationSeconds(
  subscriptionTier?: string | null,
): number {
  return subscriptionTier === "pro" || subscriptionTier === "studio" ? 600 : 180;
}

export function shouldShowVideoUploadUpsell({
  subscriptionTier,
  dismissed,
}: {
  subscriptionTier?: string | null;
  dismissed: boolean;
}): boolean {
  return !dismissed && subscriptionTier !== "pro" && subscriptionTier !== "studio";
}

export function buildVideoUploadMetadata({
  caption,
  filterState,
  overlayState,
}: BuildVideoUploadMetadataInput): {
  caption: string;
  hashtags: string[];
  filter?: VideoUploadFilterState;
  overlays?: { schemaVersion: 1; overlays: unknown[] };
} {
  const trimmedCaption = caption.trim();
  const hashtags = (trimmedCaption.match(/#\w+/g) ?? []).map((tag) =>
    tag.slice(1).toLowerCase(),
  );
  // Wrap text overlays in the v1 wire format. We avoid importing
  // overlayStateToV1 here because shared-utils must stay free of
  // shared-types runtime imports (different package boundary); inline
  // the conversion to keep the shape identical to overlayStateToV1.
  const items = (overlayState.textOverlays as Array<Record<string, unknown>>).map((t) => ({
    id: t.id,
    type: "text" as const,
    x: t.x,
    y: t.y,
    rotation: t.rotation,
    opacity: t.opacity,
    startTime: t.startTime,
    endTime: t.endTime,
    payload: {
      text: t.text,
      fontSize: t.fontSize,
      fontFamily: t.fontFamily,
      color: t.color,
      backgroundColor: t.backgroundColor,
      bold: t.bold,
      italic: t.italic,
      textAlign: t.textAlign,
    },
  }));
  return {
    caption: trimmedCaption,
    hashtags,
    ...(filterState.filterId ? { filter: filterState } : {}),
    ...(items.length > 0 ? { overlays: { schemaVersion: 1, overlays: items } } : {}),
  };
}

export function buildDraftRestoreState({
  draft,
  defaultPollDuration,
}: BuildDraftRestoreStateInput): {
  content: string;
  replyPermission: ReplyPermission;
  imageDataUrl: string | null;
  imageEntries: Array<{ uri: string; width: number; height: number }>;
  altTexts: Record<number, string>;
  video: { uri: string; width: number; height: number; duration?: number } | null;
  videoTimelineProject: TimelineProject | null;
  videoLinkOverlays: OverlayItem[];
  videoVoiceover: PostDraft["videoVoiceover"];
  videoCaption: string;
  targetCreatorPodcastId: number | null;
  pollState: ReturnType<typeof getDraftPollState>;
} {
  const imageEntries = (draft.imageUris ?? []).map((uri, index) => ({
    uri,
    width: draft.imageDimensions?.[index]?.width ?? 0,
    height: draft.imageDimensions?.[index]?.height ?? 0,
  }));

  const altTexts: Record<number, string> = {};
  Object.entries(draft.altTexts ?? {}).forEach(([key, value]) => {
    altTexts[parseInt(key)] = value;
  });

  return {
    content: draft.content,
    replyPermission: normalizeReplyPermission(draft.replyPermission),
    imageDataUrl: draft.imageDataUrl,
    imageEntries,
    altTexts,
    video: draft.videoUri
      ? {
          uri: draft.videoUri,
          width: draft.videoDimensions?.width ?? 0,
          height: draft.videoDimensions?.height ?? 0,
          duration: draft.videoDuration,
        }
      : null,
    videoTimelineProject: draft.videoTimelineProject ?? null,
    videoLinkOverlays: draft.videoLinkOverlays ?? [],
    videoVoiceover: draft.videoVoiceover ?? null,
    videoCaption: draft.videoCaption ?? draft.content,
    targetCreatorPodcastId: sanitizeDraftShowTarget(draft.targetCreatorPodcastId),
    pollState: getDraftPollState({ draft, defaultPollDuration }),
  };
}

function buildComposeImageFields(
  imageUrls: string[] | undefined,
  mode: ComposeImageFieldMode,
) {
  if (!imageUrls?.length) return {};
  if (mode === "multi-only") {
    return { imageUrls };
  }
  if (imageUrls.length === 1) {
    return { imageUrl: imageUrls[0] };
  }
  return { imageUrls };
}

export function buildComposePostBody<
  Extra extends Record<string, unknown> = Record<string, never>,
>({
  content,
  replyPermission,
  timezoneOffset,
  imageUrls,
  imageFieldMode = "single-or-multi",
  altTexts,
  poll,
  embedUrl,
  extra,
}: BuildComposePostBodyInput<Extra>) {
  return {
    content: content.trim(),
    replyPermission: normalizeReplyPermission(replyPermission),
    ...(typeof timezoneOffset === "number" ? { timezoneOffset } : {}),
    ...buildComposeImageFields(imageUrls, imageFieldMode),
    ...(altTexts?.length ? { altTexts } : {}),
    ...(poll ? { poll } : {}),
    ...(embedUrl ? { embedUrl } : {}),
    ...(extra ?? ({} as Extra)),
  };
}

const LEGACY_VIDEO_FIELDS = [
  "videoUri",
  "videoDimensions",
  "videoDuration",
  "videoVoiceover",
  "videoCaption",
  "videoLinkOverlays",
] as const;

/** Explicit draftKind ALWAYS wins; only unversioned legacy drafts use heuristics:
 *  timeline-project or any legacy video field → "video-project" (they cannot
 *  restore into the video-less update sheet); everything else → "update". */
export function classifyDraft(draft: PostDraft): DraftKind {
  if (draft.draftKind) return draft.draftKind;
  if (draft.videoTimelineProject) return "video-project";
  if (LEGACY_VIDEO_FIELDS.some((field) => (draft as unknown as Record<string, unknown>)[field] != null)) {
    return "video-project";
  }
  return "update";
}

export function routeForDraft(draft: PostDraft): {
  pathname: "/compose-update" | "/compose-episode" | "/video-compose";
  params: { draftId: string };
} {
  const kind = classifyDraft(draft);
  const pathname =
    kind === "episode" ? "/compose-episode" : kind === "video-project" ? "/video-compose" : "/compose-update";
  return { pathname, params: { draftId: draft.id } };
}

export function buildEpisodeDraft(input: { episode: EpisodeDraftData }): Omit<PostDraft, "id" | "createdAt" | "updatedAt"> {
  return {
    // Mirror the title into content so DraftsSheet previews read sensibly.
    content: input.episode.details.title,
    imageDataUrl: null,
    replyPermission: "everyone",
    draftKind: "episode",
    episode: input.episode,
  };
}
